import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { css } from '@emotion/react';

import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvIcon from 'shared/components/nv-icon';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { toggleAiFeedback } from 'redux/actions/submissions';
import { InsightVisibility } from 'redux/schemas/models/exercise';
import { NvTooltip, TextAlign } from 'shared/components/nv-tooltip';
import { semiBoldFontWeight } from 'styles/global_defaults/fonts';
import ClickableContainer from 'components/clickable-container';
import AiInsightsSetupModal from './ai-insights-setup-modal';

interface AiInsightsDropdownItemProps {
  exerciseId: string;
  lectureComponentId: string;
  isCollection?: boolean;
  isLinked?: boolean;
}

const styles = css`
  .font-semibold {
    font-weight: ${semiBoldFontWeight};
  }
`;

const getVisibilityLabel = (visibility: InsightVisibility, courseAliases: Record<string, string>) => {
  const translations = t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS;
  switch (visibility) {
    case InsightVisibility.LEARNERS:
      return translations.SETUP_DIRECT_TO_LEARNER(courseAliases);
    case InsightVisibility.ADMINS:
      return translations.SETUP_ADMINS_ONLY();
    case InsightVisibility.DISABLED:
    default:
      return translations.SETUP_DISABLED();
  }
};

const AiInsightsDropdownItem = ({ exerciseId, lectureComponentId, isCollection, isLinked }: AiInsightsDropdownItemProps) => {
  const [showModal, setShowModal] = useState(false);
  const [currentVisibility, setCurrentVisibility] = useState<InsightVisibility>(InsightVisibility.DISABLED);
  const [currentUseCustomContext, setCurrentUseCustomContext] = useState<boolean>(false);
  const [currentCustomContext, setCurrentCustomContext] = useState<string>('');
  const dispatch = useAppDispatch();

  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, currentCourse?.catalogId));
  const exercises = useSelector(state => state?.app?.lecturePage?.exercises);
  const normalizedExercise = useSelector(state => state.models.exercises[exerciseId]);

  const exerciseFromList = exercises?.find(ex => ex?.id === exerciseId);
  const currentExercise = normalizedExercise ?? exerciseFromList;

  useEffect(() => {
    if (currentExercise) {
      const fallbackVisibility = currentExercise.aiFeedbackEnabled
        ? InsightVisibility.ADMINS
        : (isCollection ? InsightVisibility.LEARNERS : InsightVisibility.DISABLED);
      setCurrentVisibility(currentExercise.insightVisibility ?? fallbackVisibility);
      setCurrentUseCustomContext(currentExercise.useCustomContext ?? false);
      setCurrentCustomContext(currentExercise.customContext ?? '');
    }
  }, [currentExercise, isCollection]);

  const handleClick = () => {
    // Dismiss the NvDropdown
    document.getElementById(`lecture-component-${lectureComponentId}`)?.click();
    setShowModal(true);
  };

  const handleSave = (
    insightVisibility: InsightVisibility,
    useCustomContext: boolean,
    customContext: string,
  ) => {
    setCurrentVisibility(insightVisibility);
    setCurrentUseCustomContext(useCustomContext);
    setCurrentCustomContext(customContext);
    setShowModal(false);
    dispatch(toggleAiFeedback({
      catalogId: currentCourse.catalogId,
      exerciseId,
      aiFeedbackEnabled: insightVisibility !== InsightVisibility.DISABLED,
      insightVisibility,
      useCustomContext: insightVisibility === InsightVisibility.DISABLED ? undefined : useCustomContext,
      customContext: useCustomContext ? customContext : null,
    }));
  };

  return (
    <>
      <ClickableContainer
        css={styles}
        className='bs4-dropdown-item d-flex pl-2 align-items-start'
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        tabIndex={0}
        role='menuitem'
        data-qa='ai-insights-setup-dropdown-item'
      >
        <div className='d-flex flex-column pr-2'>
          <div className='d-flex'>
            {(currentVisibility === InsightVisibility.DISABLED)
              ? <NvIcon size='xss-smallest' icon='gen-ai' className='pr-1 mt-1' />
              : <GradientAiIcon size='xss-smallest' icon='gen-ai' className='pr-1 mt-1' />}
            <span>{t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS.SETUP()}</span>
          </div>
          <span className='text-small text-gray-3 font-semibold'>
            {getVisibilityLabel(currentVisibility, courseAliases)}
          </span>
        </div>
      </ClickableContainer>
      <AiInsightsSetupModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        currentVisibility={currentVisibility}
        currentUseCustomContext={currentUseCustomContext}
        currentCustomContext={currentCustomContext}
        courseAliases={courseAliases}
        isCollection={!!isCollection}
        isLinked={!!isLinked}
      />
    </>
  );
};

export default AiInsightsDropdownItem;
